<template>
  <f7-page
    :page-content="true"
    name="novedades"
    class="pagina-novedades"
    @page:beforein="entrar"
    @page:beforeout="salir"
  >
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>{{ $t('novedades.titulo') }}</f7-nav-title>
    </f7-navbar>

    <f7-block strong class="no-margin-bottom">
      <p class="explicacion">{{ $t('novedades.explicacion') }}</p>
    </f7-block>

    <!-- Cargando la primera página. Mientras dure no se dice ni que hay novedades ni que no hay:
         hasta que conteste el servidor no se sabe cuál de las dos cosas es. -->
    <f7-block v-if="cargando" class="estado">
      <p>{{ $t('novedades.cargando') }}</p>
    </f7-block>

    <!-- No se ha podido leer. El reintento es un `<button>` de verdad y no un `span` anunciado como
         botón: así se alcanza con el tabulador y se dispara con Intro o con Espacio sin tener que
         implementar nada, que es lo que pone el navegador en un `<button>`. `type="button"` porque
         el tipo por defecto es `submit`: sin él, el día que esto caiga dentro de un formulario,
         reintentar lo enviaría. -->
    <f7-block v-else-if="cargaFallida" strong class="estado estado-error">
      <p>{{ $t('novedades.errorCarga') }}</p>
      <button type="button" class="boton-reintentar" @click="cargar()">
        {{ $t('novedades.reintentar') }}
      </button>
    </f7-block>

    <!-- Primera instalación: no hay ninguna versión publicada todavía. No es un fallo de carga, y
         decirlo evita que quien entre crea que la pantalla no funciona. Lo que NO se hace es
         rellenarla con ejemplos: anunciar un despliegue que no existe sería mentir. -->
    <f7-block v-else-if="sinNovedades" class="estado">
      <p>{{ $t('novedades.sinNovedades') }}</p>
    </f7-block>

    <template v-else>
      <!-- Cada versión en su tarjeta. El texto va interpolado con `{{ }}`, que Vue escapa siempre:
           aquí no hay ni un `v-html`, así que un texto con etiquetas se ve tal cual y no se
           ejecuta. -->
      <!-- `tarjetas` y no `versiones`: lo que se pinta son las notas ya resueltas —de los campos
           nuevos de la API si vienen, y de los de siempre si no—, decidido UNA vez por lectura y no
           en cada repintado. Ver `tarjetas` en el script. -->
      <f7-card v-for="(v, i) in tarjetas" :key="i" class="tarjeta-version">
        <f7-card-header class="cabecera-version">
          <div class="identidad-version">
            <span class="etiqueta-version">{{ v.version }}</span>
            <span class="fecha-version">{{ fechaEnMadrid(v.fechaDespliegue) }}</span>
          </div>
          <!-- «Vigente» y «Retirada» no pueden salir las dos: una versión retirada no es la que está
               en producción, y decir las dos cosas en la misma tarjeta sería que la pantalla se
               contradice a sí misma. La API ya manda `vigente` a false en las retiradas; aquí la
               condición se repite donde se pinta, que es donde se lee. -->
          <span v-if="v.retirada" class="sello sello-retirada">
            {{ $t('novedades.retirada') }}
          </span>
          <span v-else-if="v.vigente" class="sello sello-vigente">
            {{ $t('novedades.vigente') }}
          </span>
        </f7-card-header>
        <f7-card-content>
          <!-- Retirada: se dice POR QUÉ, junto al sello y no en un aviso al principio de la
               pantalla. Un sello que no explica nada obliga a adivinar. -->
          <p v-if="v.retirada" class="motivo-retirada">
            {{ $t('novedades.motivoRetirada') }}: {{ v.motivoRetirada }}
          </p>

          <!-- Un apartado solo aparece si tiene algo: una entrada puede traer solo mejoras o solo
               correcciones, y un título con la lista vacía debajo afirma que hubo algo que no hubo. -->
          <!-- Las referencias de tarea van DENTRO del `<li>`, junto a su texto, y no en la cabecera
               de la versión: una entrega reúne tareas distintas, así que arriba no dirían de qué sale
               cada línea. Y detrás del texto, no delante: lo que se lee primero es qué ha cambiado.
               Cuando una nota no tiene referencia —todo el historial anterior a #556— no se pinta
               nada: ni «#0» ni un hueco con la almohadilla sola. -->
          <!-- Cada cápsula lleva SU texto completo para el lector («, Tarea 58») y esconde el «#58»
               que se ve: así lo que se lee en voz alta no se arma juntando trozos de elementos
               distintos. Entre una cápsula y la siguiente —y entre el texto de la nota y la primera—
               no hay ni un espacio en el HTML, a propósito, porque la separación visual la da el
               margen; sin el separador DENTRO del texto oculto, al aplanar saldría «Tarea 58Tarea
               253» y el lector pegaría el número con la etiqueta siguiente. El separador es una coma
               y no un espacio porque un espacio al borde de un elemento se puede perder al colapsar
               los blancos, y una coma además hace pausar al lector. -->
          <div v-if="v.mejoras.length" class="grupo-novedades">
            <h4 class="titulo-grupo titulo-mejoras">{{ $t('novedades.mejoras') }}</h4>
            <ul class="lista-novedades">
              <li v-for="(n, j) in v.mejoras" :key="'m' + j">{{ n.texto }}<span
                v-if="n.tareas.length" class="referencias-tarea"
              ><span v-for="t in n.tareas" :key="t" class="referencia-tarea"><span
                class="solo-lectores"
              >{{ $t('novedades.separadorTarea') }}{{ $t('novedades.tareaOrigen') }} {{ t }}</span><span
                aria-hidden="true">#{{ t }}</span></span></span></li>
            </ul>
          </div>
          <div v-if="v.correcciones.length" class="grupo-novedades">
            <h4 class="titulo-grupo titulo-correcciones">{{ $t('novedades.correcciones') }}</h4>
            <ul class="lista-novedades">
              <li v-for="(n, j) in v.correcciones" :key="'c' + j">{{ n.texto }}<span
                v-if="n.tareas.length" class="referencias-tarea"
              ><span v-for="t in n.tareas" :key="t" class="referencia-tarea"><span
                class="solo-lectores"
              >{{ $t('novedades.separadorTarea') }}{{ $t('novedades.tareaOrigen') }} {{ t }}</span><span
                aria-hidden="true">#{{ t }}</span></span></span></li>
            </ul>
          </div>
        </f7-card-content>
      </f7-card>

      <!-- El historial ha cambiado mientras se miraba —cualquiera de las tres operaciones que
           admite: publicar, retirar o restaurar una retirada—, así que la lista se ha vuelto a
           cargar desde el principio y lo que se ve ahora NO es lo que había antes de pulsar. Eso
           hay que decirlo, y aquí: junto al botón que se ha pulsado, que es donde está mirando quien
           lo pulsó. Un aviso al principio de la pantalla no se relaciona con el clic. Va fuera de la
           cadena de abajo porque convive con el botón: el aviso explica, el botón sigue sirviendo. -->
      <f7-block v-if="historialCambiado" strong class="estado estado-aviso">
        <p>{{ $t('novedades.historialCambiado') }}</p>
      </f7-block>

      <!-- El historial anterior se mantiene y se pagina: no se descargan todos los registros de una
           vez. Mientras la página siguiente viaja se enseña el aviso en vez del botón: un botón que
           no hace nada al pulsarlo es peor que no tener botón. -->
      <f7-block v-if="cargandoMas" class="estado">
        <p>{{ $t('novedades.cargando') }}</p>
      </f7-block>
      <f7-block v-else-if="falloAlAmpliar" strong class="estado estado-error">
        <p>{{ $t('novedades.errorMas') }}</p>
        <button type="button" class="boton-reintentar" @click="verMas()">
          {{ $t('novedades.reintentar') }}
        </button>
      </f7-block>
      <f7-block v-else-if="hayMas" class="bloque-ver-mas">
        <button type="button" class="boton-ver-mas" @click="verMas()">
          {{ $t('novedades.verMas') }}
        </button>
      </f7-block>
    </template>
  </f7-page>
</template>

<style scoped>
.explicacion { font-size: 13px; color: #475569; margin: 0; }
.estado { font-size: 13px; color: #475569; }
.estado p { margin: 0 0 8px; }
.estado-error { color: #92400e; background-color: #fef3c7; }
/* El aviso de que el historial ha cambiado no es un error —la lectura fue bien— así que no se pinta
   con los colores del error: informa de por qué la lista no es la de antes. */
.estado-aviso { color: #1e3a5f; background-color: #e0f2fe; }
.estado p:last-child { margin-bottom: 0; }

/* Los dos botones son `<button>` de verdad, así que lo primero es quitarles el aspecto que les pone
   el navegador y dejar el de un enlace de la casa. Lo que no se toca es el foco: quien llega con el
   tabulador tiene que ver dónde está. */
.boton-reintentar, .boton-ver-mas {
  -webkit-appearance: none; appearance: none;
  border: 1px solid #22355b; background-color: transparent; color: #22355b;
  border-radius: 4px; padding: 6px 16px; font-size: 13px; font-weight: bold;
  cursor: pointer;
}
.boton-ver-mas { width: 100%; }
/* El `:focus` a secas es el que funciona en todas partes; el par siguiente lo retira al pulsar con
   el ratón donde el navegador sabe distinguirlo. Un navegador que no entienda `:focus-visible`
   descarta las dos reglas y se queda con el recuadro siempre, que es el fallo del lado seguro. */
.boton-reintentar:focus, .boton-ver-mas:focus { outline: 2px solid #22355b; outline-offset: 2px; }
.boton-reintentar:focus:not(:focus-visible),
.boton-ver-mas:focus:not(:focus-visible) { outline: none; }
.boton-reintentar:focus-visible,
.boton-ver-mas:focus-visible { outline: 2px solid #22355b; outline-offset: 2px; }

.cabecera-version { display: flex; align-items: center; justify-content: space-between; }
/* En móvil la cabecera se parte en dos líneas en vez de apretar la fecha contra el sello. */
.identidad-version { display: flex; flex-wrap: wrap; align-items: baseline; }
.etiqueta-version {
  display: inline-block; padding: 4px 12px; border-radius: 12px;
  font-size: 13px; font-weight: bold; color: #fff; background-color: #22355b;
}
.fecha-version { font-size: 13px; color: #64748b; margin-left: 10px; }
.sello {
  font-size: 11px; font-weight: bold; text-transform: uppercase;
  padding: 2px 8px; border-radius: 10px; white-space: nowrap;
}
.sello-vigente { color: #14532d; background-color: #dcfce7; }
.sello-retirada { color: #7f1d1d; background-color: #fee2e2; }
.motivo-retirada { font-size: 13px; color: #7f1d1d; margin: 0 0 10px; }
.grupo-novedades + .grupo-novedades { margin-top: 12px; }
.titulo-grupo { font-size: 13px; margin: 0 0 4px; }
.titulo-mejoras { color: #14532d; }
.titulo-correcciones { color: #92400e; }
.lista-novedades { margin: 0; padding-left: 20px; }
.lista-novedades li { font-size: 13px; line-height: 1.5; margin-bottom: 4px; }

/* La tarea de origen: se lee, pero es claramente secundaria al texto de la novedad —más pequeña y
   sin el color del texto—, porque lo que importa de la línea es qué ha cambiado. Cada número va en
   su propia cápsula y `nowrap` para que «#58» no se parta por la mitad al final de una línea; el
   grupo sí puede partir, que en móvil es lo que evita que una nota larga empuje la referencia fuera
   de la tarjeta. `inline-block` con `margin-left` en vez de un espacio en la plantilla: así no
   depende de que el compilador de plantillas conserve un espacio entre dos interpolaciones. Ojo:
   este margen separa lo que SE VE y nada más —quien lee en voz alta no ve márgenes—, así que la
   separación hablada va en el texto oculto de cada cápsula, no aquí. */
.referencia-tarea {
  display: inline-block; white-space: nowrap;
  margin-left: 6px; padding: 0 6px; border-radius: 8px;
  font-size: 11px; font-weight: bold; color: #334155; background-color: #e2e8f0;
}

/* Para quien usa lector de pantalla, «#58» a secas no dice nada: el lector lo puede leer como un
   signo o saltárselo. Aquí va la referencia entera y ya separada —«, Tarea 58»— sin que se vea en
   pantalla, donde la cápsula gris junto al texto ya se entiende; el «#58» visible queda
   `aria-hidden` para que no se oiga dos veces. No es `display:none` ni `visibility:hidden`, que se
   lo saltarían los dos. */
.solo-lectores {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}
.bloque-ver-mas { margin-top: 8px; }

@media (max-width: 480px) {
  .cabecera-version { flex-wrap: wrap; }
  .fecha-version { margin-left: 0; width: 100%; margin-top: 2px; }
}
</style>

<script>
import Novedades from "./../../js/db/Novedades.js";
import carrilPeticiones from "./../../js/utils/carrilPeticiones.js";

// Cuántas versiones por página. El servidor tiene su propio tope (50) y recorta lo que pase de ahí,
// así que este número solo decide cuánto se trae de golpe.
const POR_PAGINA = 10;

// En qué puede acabar una ampliación. Son tres cosas distintas y cada una se cuenta distinto, así
// que no caben en un booleano: con `true`/`false` el caso de la página que viene de otra lista se
// contaba como un fallo de lectura y se le ofrecía a la persona reintentar una página que iba a
// volver igual de descolocada.
const AMPLIADA = "ampliada";
const OTRA_PAGINA = "otraPagina";
const OTRA_LISTA = "otraLista";

/**
 * Si una página siguiente pertenece a la misma lista que lo que ya está en pantalla.
 *
 * El número de página no basta. Si entre la primera página y el clic en «ver novedades anteriores»
 * se publica una versión, el servidor contesta la página que se le ha pedido —así que `pagina`
 * cuadra— pero los índices se han corrido uno hacia abajo: la primera versión de esa página es la
 * última que ya se está enseñando. Concatenarla la repite, y además deja arriba una lista en la que
 * la versión nueva no aparece y el sello de «vigente» sigue puesto en la anterior.
 *
 * **Y `total` tampoco basta**, aunque vea esa publicación. Una **retirada** conserva el registro y
 * sólo le pone la marca: el total se queda exactamente igual mientras `retirada` y `vigente` cambian
 * de sitio. Con el total como única huella, las dos páginas pasaban por ser de la misma lista y lo
 * que ya estaba en pantalla se quedaba con el sello «En producción» puesto en la versión que acababa
 * de retirarse —y sin su «Retirada»—, que es justo lo que la tarea no admite: una versión retirada
 * no se presenta como vigente.
 *
 * Quien sí lo ve es `revisionHistorial`, que lo dice el servidor y aquí es **opaca**: no se
 * interpreta, sólo se compara. Se mueve con las tres operaciones que admite el historial —publicar,
 * retirar y restaurar una retirada—, así que si no coincide entre dos páginas, lo que hay delante es
 * de otra lista.
 *
 * Se conserva la comparación por `total` como respaldo para cuando la revisión no venga por alguno
 * de los dos lados: ve menos —no la retirada— pero sigue viendo la publicación, y es lo que deja la
 * pantalla funcionando contra una API que todavía no la devuelva. Y si tampoco consta el total, se
 * cree a quien pregunta: mismo criterio que con el número de página, y al revés se recargaría en
 * bucle por un dato que nunca va a llegar.
 */
function esDeLaMismaLista(datos, enPantalla) {
  if (typeof datos.revisionHistorial === "string"
      && typeof enPantalla.revisionHistorial === "string") {
    return datos.revisionHistorial === enPantalla.revisionHistorial;
  }
  if (typeof datos.total !== "number" || typeof enPantalla.total !== "number") return true;
  return datos.total === enPantalla.total;
}

/**
 * Las notas de un apartado de una versión, en la forma en la que las pinta la plantilla:
 * `{ texto, tareas: [58] }`, con `tareas` SIEMPRE lista y vacía cuando no consta.
 *
 * **De dónde las saca es lo que hace compatible esta pantalla con las dos APIs** (#556). La API nueva
 * manda las notas dos veces: las listas de siempre, de cadenas (`mejoras`, `correcciones`), y unos
 * campos nuevos con el texto y las tareas (`mejorasConTareas`, `correccionesConTareas`). Aquí se
 * prefiere el campo nuevo **si está**, y si no se usa el anterior, que es lo que deja la pantalla
 * funcionando contra la API que todavía no ha salido —sin referencias, que es lo que hay—.
 *
 * Se mira que el campo nuevo **esté**, no que traiga algo: una versión puede no tener mejoras, y con
 * «si viene con algo» una entrega sin mejoras caería al campo anterior sin motivo. La API nueva manda
 * los dos campos siempre, también vacíos, justamente para que esto se pueda decidir así.
 *
 * Y se admite de los dos campos cualquiera de las dos formas —cadena u objeto— porque esta función es
 * lo único que las distingue: así una API que mandara el campo nuevo con cadenas, o el anterior con
 * objetos, pinta el texto en vez de «[object Object]». Lo que nunca hace es juntar las dos listas: se
 * elige una, así que ninguna nota se pinta dos veces.
 *
 * Los números se filtran: lo que no sea un entero positivo no se enseña. En una pantalla una
 * referencia falsa es peor que ninguna, y aquí no se deduce ni se inventa ninguna —el número sale del
 * dato, nunca del texto—.
 */
function notasDe(version, campoConTareas, campoAnterior) {
  const v = version || {};
  const lista = Array.isArray(v[campoConTareas]) ? v[campoConTareas] : v[campoAnterior];
  if (!Array.isArray(lista)) return [];
  return lista.map(function(n) {
    if (typeof n === "string") return { texto: n, tareas: [] };
    const nota = n || {};
    const tareas = Array.isArray(nota.tareas) ? nota.tareas : [];
    return {
      texto: typeof nota.texto === "string" ? nota.texto : "",
      tareas: tareas.filter(function(t) {
        return typeof t === "number" && isFinite(t) && t > 0 && Math.floor(t) === t;
      })
    };
  });
}

/**
 * Apartado Novedades (#250): qué mejoras y correcciones se han publicado en producción, por fecha y
 * versión.
 *
 * Cada nota enseña además, si consta, la tarea de la que sale, como `#58` (#556). El número viene en
 * la respuesta de la API: desde aquí no se consulta GitHub ni hace falta ninguna credencial para
 * pintarlo. Ver `notasDe()` para de qué campo sale cada cosa y qué pasa con las notas que no la
 * traen, que son todas las anteriores a esa ampliación.
 *
 * Solo lee. La publicación la hace una identidad de servicio contra otra ruta de la API, y desde
 * aquí no hay forma de llamarla: ni ruta ni secreto. Es a propósito —un token de publicación en el
 * front sería un token en el navegador de toda la plantilla—, y es también por lo que esta pantalla
 * no consulta GitHub: lo que enseña sale de la base de la propia aplicación.
 *
 * Las peticiones van por `carrilPeticiones`, que **solo contesta la última**. Hace falta porque
 * «Reintentar» y «Ver más» se pueden pulsar dos veces seguidas y las respuestas no vuelven en orden:
 * sin el carril, una respuesta vieja llegaba después y pintaba una página que ya no era la de delante.
 */
export default {
  name: "novedades",
  data() {
    return {
      versiones: [],
      // La última página que se ha traído BIEN. `verMas` pide la siguiente a partir de aquí, así que
      // una ampliación que falla no adelanta el contador y reintentar vuelve a pedir la misma.
      ultimaPagina: -1,
      /**
       * Cuántas versiones hay en el historial según la última lectura buena. No se pinta: es el
       * respaldo de la huella de la lista cuando el servidor no manda `revisionHistorial` (ver
       * `esDeLaMismaLista`). `null` cuando no consta.
       */
      total: 0,
      /**
       * Con qué estado del historial se montó lo que hay en pantalla, según la última lectura buena.
       * Es la huella que de verdad decide si una página siguiente es de la misma lista: la manda el
       * servidor, no se interpreta y sólo se compara. `null` cuando no consta.
       */
      revisionHistorial: null,
      hayMas: false,
      /** La primera carga está en vuelo: todavía no se sabe si hay novedades o no. */
      cargando: false,
      /** Una página siguiente está en vuelo. */
      cargandoMas: false,
      /** La primera carga ha fallado y lo que hay delante no vale. */
      cargaFallida: false,
      /** La ampliación ha fallado: lo que ya está en pantalla sigue valiendo. */
      falloAlAmpliar: false,
      /**
       * El historial cambió mientras se miraba y la lista se ha recargado desde el principio. No es
       * un fallo: es que lo que se ve ahora no es lo que había al pulsar «ver más», y callarlo
       * dejaría una lista que encoge sola, o un sello «En producción» en una versión retirada.
       */
      historialCambiado: false,
      /** Alguna carga ha terminado bien: hasta entonces no se puede decir «no hay novedades». */
      yaCargado: false,
      /** Si la pantalla está delante. La ruta es `keepAlive`, así que sobrevive a salir y volver. */
      activa: false,
      carril: carrilPeticiones()
    };
  },
  computed: {
    /**
     * «Todavía no hay novedades» solo se puede decir con una lectura terminada y nada en ella. Las
     * tres condiciones hacen falta: mientras se carga no se sabe, con la carga fallida lo que hay
     * que decir es que no se ha podido leer, y una lista con algo no está vacía.
     */
    sinNovedades() {
      return this.yaCargado && !this.cargando && !this.cargaFallida && this.versiones.length === 0;
    },
    /**
     * Lo que pinta la plantilla: cada versión con sus notas ya resueltas y normalizadas, en vez de la
     * respuesta de la API tal cual.
     *
     * Aquí es donde se decide de qué campo salen las notas (ver `notasDe`), y se decide **una vez por
     * lectura**: en la plantilla se preguntaba dos veces por cada apartado —una para saber si se pinta
     * el título y otra para recorrer la lista— y cada repintado volvía a resolverlas.
     *
     * Se copian sólo los campos que se enseñan. Lo que la API mande y no esté aquí no llega a la
     * pantalla, que es la misma frontera que tiene la API por el otro lado.
     */
    tarjetas() {
      return this.versiones.map(function(v) {
        const version = v || {};
        return {
          version: version.version,
          fechaDespliegue: version.fechaDespliegue,
          vigente: version.vigente === true,
          retirada: version.retirada === true,
          motivoRetirada: version.motivoRetirada,
          mejoras: notasDe(version, "mejorasConTareas", "mejoras"),
          correcciones: notasDe(version, "correccionesConTareas", "correcciones")
        };
      });
    }
  },
  methods: {
    /** Entrar en la pantalla. Se recarga siempre: el historial pudo crecer desde la última visita. */
    entrar: function() {
      this.activa = true;
      this.cargar();
    },
    /**
     * Salir. Se limpia desde `@page:beforeout` y no desde `deactivated`: el keepAlive de Framework7
     * no monta ni desmonta el componente, así que los ganchos de Vue no llegan a dispararse.
     *
     * Lo que se invalida es la petición en vuelo, y con ella **los avisos que la describían**: un
     * «Cargando…» que se queda esperando una respuesta que ya no cuenta deja la pantalla sin un solo
     * sitio donde pulsar al volver a entrar. La lista no se vacía: al entrar se recarga.
     */
    salir: function() {
      this.activa = false;
      this.carril.invalidar();
      this.cargando = false;
      this.cargandoMas = false;
    },
    /**
     * La primera página. Reemplaza lo que haya: es la lectura que manda.
     *
     * Antes de lanzarla se baja `cargaFallida`, y no al contestar: si no, durante todo el viaje del
     * reintento la pantalla sigue diciendo «no se ha podido leer» y ofreciendo reintentar lo que ya
     * está reintentando.
     */
    cargar: function() {
      const self = this;
      self.cargando = true;
      self.cargaFallida = false;
      // Una ampliación en vuelo deja de contar: el carril la descarta, y su aviso se va con ella.
      self.cargandoMas = false;
      self.falloAlAmpliar = false;
      // Esta lectura es la que manda, así que el aviso de que la anterior era de otra lista ya no
      // explica nada de lo que se va a ver. Quien recarga por el cambio del historial lo vuelve a
      // poner después, que es el único caso en el que sigue valiendo.
      self.historialCambiado = false;
      self.carril.lanzar(Novedades.pagina(0, POR_PAGINA), {
        exito: function(pagina) {
          // Mismo criterio que al ampliar: si la respuesta dice ser de otra página, no es la
          // primera y pintarla como si lo fuera enseñaría un trozo del historial por el principio.
          // Aquí sí es un fallo de carga —lo que hay delante dependía de esta lectura—, así que se
          // ofrece reintentar en vez de dar por buena la lista.
          if (!self.aplicarPrimeraPagina(pagina)) {
            self.cargando = false;
            self.cargaFallida = true;
            return;
          }
          self.cargando = false;
          self.yaCargado = true;
        },
        limpiar: function() {
          // Lo que hay delante dependía de esta lectura y ya no vale. Se vacía, porque si no la
          // pantalla enseñaría el historial de antes junto al aviso de que no se ha podido leer.
          self.versiones = [];
          self.total = 0;
          self.revisionHistorial = null;
          self.hayMas = false;
          self.ultimaPagina = -1;
        },
        fallo: function() {
          self.cargando = false;
          self.cargaFallida = true;
        },
        descartada: function() {
          // Una lectura que llega tarde no deja nada pendiente: la vigente pinta lo bueno. Lo único
          // que hay que hacer es no dejar el «Cargando…» de esta puesto si nadie la espera.
          if (!self.activa) self.cargando = false;
        }
      });
    },
    /**
     * La página siguiente, que se AÑADE a lo que ya hay. Un fallo aquí no invalida lo que está en
     * pantalla —se leyó bien— así que no se vacía nada: se ofrece reintentar esa página.
     */
    verMas: function() {
      const self = this;
      if (self.cargando || self.cargandoMas) return;
      const siguiente = self.ultimaPagina + 1;
      self.cargandoMas = true;
      self.falloAlAmpliar = false;
      // Quien vuelve a pulsar ya ha visto el aviso del cambio: dejarlo puesto lo convertiría en un
      // adorno permanente que no se refiere a nada.
      self.historialCambiado = false;
      self.carril.lanzar(Novedades.pagina(siguiente, POR_PAGINA), {
        exito: function(pagina) {
          const resultado = self.anadirPagina(pagina, siguiente);
          self.cargandoMas = false;
          // El historial ha cambiado mientras se miraba —se ha publicado, se ha retirado o se ha
          // restaurado una versión; aquí no se sabe cuál de las tres y el aviso tampoco lo afirma—:
          // esta página no es de la misma lista que lo que hay delante y no hay forma de
          // pegarla donde iba. La única lista coherente que se puede enseñar es la nueva desde el
          // principio, así que se recarga —y se dice, que si no la lista encogería sola, o se
          // quedaría un sello «En producción» en una versión retirada—. No es un fallo de
          // ampliación: la lectura fue bien, lo que ha cambiado es lo que hay detrás.
          if (resultado === OTRA_LISTA) {
            self.cargar();
            self.historialCambiado = true;
            return;
          }
          // Una respuesta que no es de la página que se pidió no amplía nada: se trata como un
          // fallo de ampliación, que es lo que es. Lo que había en pantalla se leyó bien y sigue
          // valiendo, así que no se vacía: se ofrece reintentar esa misma página.
          self.falloAlAmpliar = resultado !== AMPLIADA;
        },
        // Sin `limpiar`: lo que hay en pantalla no dependía de esta lectura.
        fallo: function() {
          self.cargandoMas = false;
          self.falloAlAmpliar = true;
        },
        descartada: function() {
          if (!self.activa) self.cargandoMas = false;
        }
      });
    },
    /**
     * Si una respuesta es de la página que se pidió. El servidor devuelve en `pagina` la que ha
     * servido, así que cuando lo diga y no case —porque recorte el índice, o porque conteste a otra
     * cosa— lo que trae no es lo que se le pidió y no se puede colocar donde iba: concatenarlo
     * igual mete duplicados o se salta registros, que es el fallo que no se ve hasta que alguien
     * cuenta las versiones. Si no lo dice, se cree a quien pregunta.
     */
    esDeLaPagina: function(datos, pedida) {
      return typeof datos.pagina !== "number" || datos.pagina === pedida;
    },
    /**
     * La respuesta de la primera página. Una respuesta vacía o rara se trata como «no hay nada», y
     * una que diga ser de otra página no se pinta: devuelve `false` y quien llama la da por fallida.
     */
    aplicarPrimeraPagina: function(pagina) {
      const datos = pagina || {};
      if (!this.esDeLaPagina(datos, 0)) return false;
      this.versiones = Array.isArray(datos.versiones) ? datos.versiones : [];
      // `null` y no 0 si no consta: 0 es un total de verdad —el historial vacío— y confundirlo con
      // «no lo sé» haría que la página siguiente pareciera de otra lista.
      this.total = typeof datos.total === "number" ? datos.total : null;
      // Igual que el total: `null` si no consta, para que la página siguiente caiga en el respaldo
      // en vez de compararse contra un dato inventado.
      this.revisionHistorial = typeof datos.revisionHistorial === "string"
        ? datos.revisionHistorial : null;
      this.hayMas = datos.hayMas === true;
      this.ultimaPagina = 0;
      return true;
    },
    /**
     * Añade una página al final, **solo** si la respuesta es la de la página que se pidió Y de la
     * misma lista: así una respuesta que no cuadre no deja un hueco en el historial ni repite lo que
     * ya está. Cuando no amplía no toca ni la lista ni los contadores, y `ultimaPagina` se queda
     * donde estaba para que reintentar vuelva a pedir la misma.
     *
     * Devuelve cuál de las tres cosas ha pasado, porque cada una se cuenta distinto: `AMPLIADA`,
     * `OTRA_PAGINA` (fallo de ampliación, se ofrece reintentar) o `OTRA_LISTA` (el historial ha
     * cambiado, hay que recargar desde el principio).
     */
    anadirPagina: function(pagina, pedida) {
      const datos = pagina || {};
      if (!this.esDeLaPagina(datos, pedida)) return OTRA_PAGINA;
      if (!esDeLaMismaLista(datos, this)) return OTRA_LISTA;
      const nuevas = Array.isArray(datos.versiones) ? datos.versiones : [];
      this.versiones = this.versiones.concat(nuevas);
      this.total = typeof datos.total === "number" ? datos.total : this.total;
      this.revisionHistorial = typeof datos.revisionHistorial === "string"
        ? datos.revisionHistorial : this.revisionHistorial;
      this.hayMas = datos.hayMas === true;
      this.ultimaPagina = pedida;
      return AMPLIADA;
    },
    /**
     * La fecha del despliegue en Europe/Madrid, que es la zona en la que trabaja la empresa. La API
     * la manda en ISO-8601 UTC con la `Z` puesta, así que aquí no hay que suponer ninguna zona: hay
     * que convertirla.
     *
     * Con `Intl` y la zona dicha por su nombre, para no cablear el cambio de hora: en verano Madrid
     * va a +02:00 y en invierno a +01:00, y una suma fija de horas acertaría media parte del año. Si
     * el navegador no admite la zona por nombre —un WebView viejo de Cordova—, se cae al formato
     * local del dispositivo, que es lo más cerca que se puede estar sin inventarse el desplazamiento.
     */
    fechaEnMadrid: function(iso) {
      if (!iso) return "";
      const fecha = new Date(iso);
      if (isNaN(fecha.getTime())) return "";
      const opciones = {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      };
      try {
        return new Intl.DateTimeFormat("es-ES",
          Object.assign({ timeZone: "Europe/Madrid" }, opciones)).format(fecha);
      } catch (sinZonaPorNombre) {
        return new Intl.DateTimeFormat("es-ES", opciones).format(fecha);
      }
    }
  }
};
</script>
